import axios from 'axios'

import { AxiosInterceptor } from '../Config/AxiosInterceptor'
import { UpdateUserPlanFormType } from '../Pages/Frontend/Components/User/EditUserPlan/EditUserPlan'
import { addCustomerFormType } from '../Pages/Frontend/Components/User/AddUser/AddUser'
import { updateKycType } from 'Pages/Frontend/Interface/Users/UpdateKYC/UpdateKYC'

AxiosInterceptor()

const customerService = {
  index: async (
    pageNumber: number,
    sortOrder: string,
    loanProvider?: string,
    tradersCardNumberStatus?: boolean,
    notificationsDisabled?: boolean,
    accountPlan?: string | undefined,
  ) => {
    // Initialize query parameters
    const params: Record<string, any> = {
      pageNumber,
      sortOrder,
    }

    // Conditionally add parameters if they are defined
    if (loanProvider) params.loanProvider = loanProvider
    if (tradersCardNumberStatus !== undefined)
      params.hasPlpTradersCardNumber = tradersCardNumberStatus
    if (notificationsDisabled !== undefined) params.notificationsDisabled = notificationsDisabled

    if (accountPlan && accountPlan !== 'All') params.accountPlan = accountPlan
    // Construct the query string
    const queryString = new URLSearchParams(params).toString()

    // Make the API request
    return await axios.get(`/customers?${queryString}`)
  },

  async exportCustomers(sortOrder: string, accountPlan: string | undefined) {
    if (accountPlan) {
      return await axios.get(
        `/customers/export/csv?sortOrder=${sortOrder}&accountPlan=${accountPlan}`,
      )
    } else {
      return axios.get(`/customers/export/csv?sortOrder=${sortOrder}`)
    }
  },

  investors: async (
    pageNumber: number,
    sortOrder: string,
    loanProvider?: string,
    tradersCardNumberStatus?: boolean,
    notificationsDisabled?: boolean,
  ) => {
    // Initialize query parameters
    const params: Record<string, any> = {
      pageNumber,
      sortOrder,
      accountPlan: 'LEASE_INVESTOR',
    }

    // Conditionally add parameters if they are defined
    if (loanProvider) params.loanProvider = loanProvider
    if (tradersCardNumberStatus !== undefined)
      params.hasPlpTradersCardNumber = tradersCardNumberStatus
    if (notificationsDisabled !== undefined) params.notificationsDisabled = notificationsDisabled

    // Construct the query string
    const queryString = new URLSearchParams(params).toString()

    // Make the API request
    return await axios.get(`/customers?${queryString}`)
  },

  // index: async (
  //   pageNumber: number,
  //   sortOrder: string,
  //   loanProvider?: string | undefined,
  //   tradersCardNumberStatus?: boolean | undefined,
  //   notificationsDisabled?: boolean | undefined,
  // ) => {
  //   if (!loanProvider) {
  //     if (tradersCardNumberStatus !== undefined) {
  //       return await axios.get(
  //         `/customers?pageNumber=${pageNumber}&sortOrder=${sortOrder}&hasPlpTradersCardNumber=${tradersCardNumberStatus}&notificationsDisabled=${notificationsDisabled}`,
  //       )
  //     }
  //     return await axios.get(
  //       `/customers?pageNumber=${pageNumber}&sortOrder=${sortOrder}&notificationsDisabled=${notificationsDisabled}`,
  //     )
  //   } else {
  //     if (tradersCardNumberStatus !== undefined) {
  //       return await axios.get(
  //         `/customers?pageNumber=${pageNumber}&sortOrder=${sortOrder}&loanProvider=${loanProvider}&hasPlpTradersCardNumber=${tradersCardNumberStatus}&notificationsDisabled=${notificationsDisabled}`,
  //       )
  //     }
  //     return await axios.get(
  //       `/customers?pageNumber=${pageNumber}&sortOrder=${sortOrder}&loanProvider=${loanProvider}&notificationsDisabled=${notificationsDisabled}`,
  //     )
  //   }
  // },

  allCustomers: () => {
    return axios.get('/customers?pageNumber=1&sortOrder=DESC')
  },

  allReferrals: async (pageNumber: number, userId: string | undefined) => {
    return await axios.get(`/customers?pageNumber=${pageNumber}&sortOrder=DESC&referrals=${userId}`)
  },

  createNewCustomer: (payload: addCustomerFormType) => {
    return axios.post('/customers', payload)
  },

  searchCustomer: async (
    endpoint: string,
    pageNumber: number,
    sortOrder: string,
    accountPlan?: string | undefined,
  ) => {
    let queryParams = `${endpoint}&pageNumber=${pageNumber ?? 1}&sortOrder=${sortOrder}`
    if (accountPlan) {
      queryParams += `&accountPlan=${accountPlan}`
    }
    return await axios.get(`customers?${queryParams}`)
  },

  updatePlan: (msisdn: string | undefined, payload: UpdateUserPlanFormType) => {
    return axios.put(`/customers/accountplan?msisdn=${msisdn}`, payload)
  },

  getDetails: (customerAccountId: string | undefined) => {
    return axios.get(`/customers?customerAccountId=${customerAccountId}&pageNumber=1`)
  },

  getKYC: (customerAccountId: string | undefined) => {
    return axios.get(`/customers/${customerAccountId}/kyc`)
  },

  updateKYC: async (customerAccountId: string | undefined, data: updateKycType) => {
    return await axios.put(`/customers/${customerAccountId}/kyc`, data)
  },

  updateTier: async (msisdn: string | undefined, tier: string, productType: string) => {
    return await axios.put(`/customers/${msisdn}/cashbacktier/${tier}/productType/${productType}`)
  },

  updateTestTier: async (
    msisdn: string | undefined,
    data: {
      cashbackTierEndDate: string
      cashbackTierStartDate: string
    },
  ) => {
    return await axios.post(`/customers/${msisdn}/test-tier-update`, data)
  },

  setBoastCycleStateDate: async (
    msisdn: string | undefined,
    data: {
      cashbackTierStartDate: string
    },
  ) => {
    return await axios.post(`/customers/${msisdn}/set-boost-cycle-start-date`, data)
  },

  getCashbackProgress: async (msisdn: string | undefined, productType: string) => {
    return await axios.get(`/customers/${msisdn}/cashbackProgress?productType=${productType}`)
  },

  getUserLPs: (msisdn: string | undefined) => {
    return axios.get(`/customers/${msisdn}/loanproviders`)
  },

  updateMultiPlp: (msisdn: string | undefined, enableOrDisable: string) => {
    return axios.post(`/customers/${msisdn}/enableMultiplePLP/${enableOrDisable}`)
  },

  searchCustomerName: async (
    firstName: string | undefined,
    lastName: undefined | string,
    pageNumber: number,
    sortOrder: string,
  ) => {
    let url = 'customers'

    if (firstName) {
      url += `?firstName=${firstName}`
    }

    if (lastName) {
      url += firstName ? `&lastName=${lastName}` : `?lastName=${lastName}`
    }

    url += `&pageNumber=${pageNumber ?? 1}&sortOrder=${sortOrder}`
    return await axios.get(url)
  },

  refreshConsentLink: (msisdn: string | undefined, loanProvider: string | undefined) => {
    return axios.put(`/customers/${msisdn}/loanprovider/${loanProvider}/bvnConsentLink`)
  },

  reconcileAccount: (msisdn: string | undefined) => {
    return axios.get(`/loanProviders/reconcileVfdAccountWithTradegridWallet/${msisdn}`)
  },

  cityLaunchers: async (pageNumber: number, sortOrder: string) => {
    return axios.get(`/cityLauncherApplications?pageNumber=${pageNumber}&sortOrder=${sortOrder}`)
  },

  cityLaunchersDetails: async (msisdn: string | undefined) => {
    return axios.get(`/cityLauncherApplications?pageNumber=1&sortOrder=DESC&phoneNumber=${msisdn}`)
  },

  cityLaunchersDelete: async (id: string) => {
    return axios.delete(`/cityLauncherApplications/${id}`)
  },

  setNotification: async (msisdn: string | undefined, notificationsDisabled: boolean) => {
    return axios.put(`/customers/${msisdn}/notificationS/${notificationsDisabled}`)
  },

  transferLoanBalance: async (
    msisdn: string | undefined,
    data: {
      amountMajor: number
    },
  ) => {
    return await axios.post(`/customers/${msisdn}/transfer-to-loan-wallet`, data)
  },

  customerReferal: async (pageNumber: number | undefined = 1, status?: string | undefined) => {
    if (status === undefined || status === 'ALL') {
      return await axios.get(`/customer-referral?pageNumber=${pageNumber}&sortOrder=DESC`)
    }
    return await axios.get(
      `/customer-referral?pageNumber=${pageNumber}&sortOrder=DESC&status=${status}`,
    )
  },

  customerReferalSingle: async (id: string | undefined) => {
    return axios.get(`/customer-referral/${id}`)
  },

  getPaystackAccount: async (msisdn: string | undefined) => {
    return await axios.get(`/customers/${msisdn}/paystack-account`)
  },

  getNombaAccount: async (msisdn: string | undefined) => {
    return await axios.get(`/customers/${msisdn}/nomba-account`)
  },

  toggleWithdrawal: async (msisdn: string | undefined, enableOrDisable: string) => {
    return await axios.put(`/customers/${msisdn}/withdrawalEnabled/${enableOrDisable}`)
  },

  defaultAccount: async (msisdn: string | undefined, account: string) => {
    return await axios.put(`/customers/${msisdn}/dedicatedAccountProvider/${account}`)
  },
}

export default customerService
